/**
 * The contractors' work, month by month — feature 29 (29 Sep 2026).
 *
 * Jérémy wanted to see whether each contractor's work moves, week after week. Worked out over
 * six mockups the same day (mockups/evolution-entrepreneurs.html), it became a dialog behind
 * the fourth tile: one line per contractor, the punches they finished each month, whether that
 * is rising, their open punches, the ones they never touched and the ones sent back — above
 * the in-and-out chart, which moved into the same dialog.
 *
 * ⚠ "Finished" is the page's own measure, `eventDayOf(row, "done")`: marked Done in Monday and
 * not sent back with Redo, on the day the Done was set. A cell of this table, the "done" bar of
 * the in-and-out chart and the period filter a click sets are one definition, not three.
 *
 * ⚠ Only what is declared in Monday is visible: a fix made on site but never marked Done
 * counts zero. Measured 29 Sep 2026: 584 of the 659 open punches carry no contractor status
 * at all, and two contractors had marked one punch Done in ten months.
 *
 * ⚠ Every figure a click can reach counts exactly what the click shows — the page's one rule
 * (25 Sep 2026) — and every click sets the page's ONE filter (22 Sep 2026): the dialog keeps
 * no state of its own. `workBase`, `workCellFilter` and `untouchedFilter` are that contract,
 * and the tests check it as a property.
 *
 * Pure: no clock. `runDate` is the snapshot's day.
 */
import { STATUS_CONTRACTOR, isContractorDone } from "./quality-audits.js";
import { eventDayOf, flowWindow, lastCompleteWeek, periodKeyOf } from "./quality-flow.js";
import { applyFilter, choose } from "./quality-report.js";
import { dateOnly, isoDateUtc } from "./time.js";
function addDays(day, n) {
    return isoDateUtc(new Date(dateOnly(day).getTime() + n * 86_400_000));
}
/**
 * The four complete weeks before the snapshot, and the four before those.
 *
 * ⚠ Complete weeks only: the week in progress holds a day or two, and comparing it with whole
 * weeks would call every Tuesday a fall. Four weeks rather than one, because the work comes in
 * bursts — Jacops finished 62 punches in June 2026 and 1 in September — and one week against
 * the week before mostly compares a zero with a zero.
 */
export function trendWeeks(runDate) {
    const last = lastCompleteWeek(runDate);
    const weeks = (skip) => Array.from({ length: 4 }, (_, i) => periodKeyOf(addDays(last.first, -7 * (skip + 3 - i)), "week"));
    return { recent: weeks(0), before: weeks(4) };
}
function lineOf(rows, months, weeks) {
    const monthIndex = new Map(months.map((m, i) => [m.key, i]));
    const recentWeeks = new Set(weeks.recent);
    const beforeWeeks = new Set(weeks.before);
    const done = months.map(() => 0);
    let open = 0;
    let untouched = 0;
    let sentBack = 0;
    let recent = 0;
    let before = 0;
    for (const row of rows) {
        const day = eventDayOf(row, "done");
        if (day !== null) {
            const i = monthIndex.get(periodKeyOf(day, "month"));
            if (i !== undefined)
                done[i]++;
            const week = periodKeyOf(day, "week");
            if (recentWeeks.has(week))
                recent++;
            else if (beforeWeeks.has(week))
                before++;
        }
        if (!row.isOpen)
            continue;
        open++;
        if (row.statusContractor === null)
            untouched++;
        else if (row.statusContractor === STATUS_CONTRACTOR.DONE && !isContractorDone(row))
            sentBack++;
    }
    const verdict = recent === 0 && before === 0 ? "none" : recent > before ? "up" : recent < before ? "down" : "flat";
    return {
        done,
        open,
        untouched,
        sentBack,
        trend: { recent, before, recentWeeks: weeks.recent, beforeWeeks: weeks.before, verdict },
    };
}
/**
 * The table, from the rows it is given — hand it `workBase(rows, filter)`.
 *
 * The months are the in-and-out chart's, so the table and the chart under it always cover the
 * same months. The contractors come from the rows, never from a list: a partner added to the
 * board on Tuesday is a line on Wednesday.
 */
export function workTable(rows, runDate, count = 12) {
    const months = flowWindow(runDate, "month", count);
    const weeks = trendWeeks(runDate);
    const byPartner = new Map();
    for (const row of rows) {
        const mine = byPartner.get(row.buildContractor);
        if (mine)
            mine.push(row);
        else
            byPartner.set(row.buildContractor, [row]);
    }
    const contractors = [...byPartner.entries()]
        .map(([partner, mine]) => ({ partner, ...lineOf(mine, months, weeks) }))
        .sort((a, b) => {
        if (a.partner === null)
            return 1;
        if (b.partner === null)
            return -1;
        return b.open - a.open || a.partner.localeCompare(b.partner);
    });
    return { months, contractors, total: lineOf(rows, months, weeks) };
}
// ─────────────────────────────────────────────────────────────────────────────
// What the table counts, and what its clicks set
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The rows the table counts: every filter but the ones a click on it sets or clears.
 *
 * `facetBase`'s rule, applied to a table. Its clicks set the partner — which, through
 * `choose`, also clears the commune and the priority — the contractor status, the period and
 * "open only"; so none of those may narrow what it counts, or a cell would show one number and
 * its click another. The Type VLS/FO and the Unifiber status still do. Counted under a
 * contractor-status filter the table would go blank ("no status" rows are never Done), and
 * under a partner it would lose every other line.
 */
export function workBase(rows, filter) {
    return applyFilter(rows, {
        ...choose(filter, { partner: undefined }),
        statusContractor: undefined,
        period: undefined,
        openOnly: false,
    });
}
/** A contractor's month, clicked: the punches they finished that month, open or closed. */
export function workCellFilter(filter, partner, monthKey) {
    return choose(filter, {
        partner,
        statusContractor: undefined,
        period: { event: "done", unit: "month", key: monthKey },
    });
}
/**
 * A contractor's "never touched", clicked: their open punches with no contractor status —
 * the list to send them.
 */
export function untouchedFilter(filter, partner) {
    return choose(filter, { partner, statusContractor: null, period: undefined, openOnly: true });
}
//# sourceMappingURL=quality-work.js.map